import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { taskService } from '@/services'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import { PageContainer, PageHeader } from '@/components/layout'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog'
import { TaskForm, type TaskFormData } from '@/components/tasks/TaskForm'

export default function TaskCreatePage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  // Track unsaved changes
  const unsavedChanges = useUnsavedChanges({
    isDirty: isDirty && !loading,
  })

  const handleSubmit = async (data: TaskFormData) => {
    if (!user) return

    setLoading(true)
    try {
      const newTask = await taskService.createTask(data, user.id)

      // Allow navigation before resetting dirty state and navigating
      unsavedChanges.allowNavigation()
      setIsDirty(false)

      toast.success('Task created successfully')
      navigate(`/tasks/${newTask.id}`)
    } catch (error) {
      console.error('Failed to create task:', error)
      toast.error('Failed to create task')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/tasks')
  }

  return (
    <PageContainer maxWidth="full">
      <PageHeader title="Create New Task" />

      <TaskForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onDirtyChange={setIsDirty}
        loading={loading}
        title="Task Details"
        description="Fill in the details below to create a new task"
      />

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={unsavedChanges.showDialog}
        onOpenChange={(open) => !open && unsavedChanges.handleCancelNavigation()}
        onConfirm={unsavedChanges.handleConfirmNavigation}
        onCancel={unsavedChanges.handleCancelNavigation}
      />
    </PageContainer>
  )
}
